/* eslint-disable import/no-internal-modules */
import { scaleLinear, scaleOrdinal, scaleThreshold } from '@visx/scale';
import { SYSTEM_COLOR } from '@askyourteam-nz/react-library/dist/lib';

export { SYSTEM_COLOR };
export { MIME_TYPE_EXTENSIONS } from '@askyourteam-nz/react-library/dist/lib';
/* eslint-enable import/no-internal-modules */

export const FONT_FAMILY = '"Fira Sans", sans-serif';

export const CUSTOMER_SUCCESS_NAME = 'Customer Success Specialist';
export const CUSTOMER_SUCCESS_TEAM = 'Customer Success Team';

export const DEFAULT_LOCALE = 'en';

export const USER_ROLE = {
  SUPER_ADMIN: 'admin',
  ACCOUNT_ADMIN: 'account_admin',
  ACCOUNT_MANAGER: 'account_manager',
  SURVEY_ADMINISTRATOR: 'survey_administrator',
  REPORT_VIEWER: 'user',
  ACTION_CONTRIBUTOR: 'action_contributor',
  LITE_USER: 'lite'
};

export const ACCOUNT_TYPE = {
  NORMAL: 'normal',
  MAC_ACCOUNT: 'mac_account',
  PATIENT_VOICE: 'patient_voice',
  ETHICAL_VOICE: 'ethical_voice'
};

export const ADMIN_ROLES = [USER_ROLE.SUPER_ADMIN, USER_ROLE.ACCOUNT_ADMIN, USER_ROLE.ACCOUNT_MANAGER, USER_ROLE.SURVEY_ADMINISTRATOR];

export const QUESTION_TYPES = {
  FREE_TEXT: 'free-text',
  ASKYOURTEAM_SCALE: 'ayt-0-6',
  ZERO_TO_SIX: '0-6',
  ZERO_TO_THREE: '0-3',
  ZERO_TO_FIVE: '0-5',
  ZERO_TO_TEN: '0-10',
  CATEGORISED: 'categorized',
  YES_NO: 'yes-no',
  MULTIPLE_CHOICE: 'multiple-choice',
  MATRIX: 'matrix',
  NPS: 'nps',
  RANKING: 'ranking'
};

export const QUESTION_LABELS = {
  AGREE_DISAGREE: 'Agree-Disagree',
  ZERO_TO_TEN: '0-10',
  ZERO_TO_THREE: '0-3',
  ZERO_TO_FIVE: '0-5',
  YES_NO: 'Yes-No',
  MULTIPLE_CHOICE: 'Multi-choice',
  FREE_TEXT: 'Free-text',
  MATRIX: 'Matrix',
  CATEGORISED: 'Categorised',
  RANKING: 'Ranking',
  NPS: 'NPS'
};

export const QUESTION_TYPE_TO_LABEL = {
  [QUESTION_TYPES.FREE_TEXT]: QUESTION_LABELS.FREE_TEXT,
  [QUESTION_TYPES.ASKYOURTEAM_SCALE]: QUESTION_LABELS.AGREE_DISAGREE,
  [QUESTION_TYPES.ZERO_TO_SIX]: QUESTION_LABELS.AGREE_DISAGREE,
  [QUESTION_TYPES.ZERO_TO_THREE]: QUESTION_LABELS.ZERO_TO_THREE,
  [QUESTION_TYPES.ZERO_TO_FIVE]: QUESTION_LABELS.ZERO_TO_FIVE,
  [QUESTION_TYPES.ZERO_TO_TEN]: QUESTION_LABELS.ZERO_TO_TEN,
  [QUESTION_TYPES.CATEGORISED]: QUESTION_LABELS.CATEGORISED,
  [QUESTION_TYPES.YES_NO]: QUESTION_LABELS.YES_NO,
  [QUESTION_TYPES.MULTIPLE_CHOICE]: QUESTION_LABELS.MULTIPLE_CHOICE,
  [QUESTION_TYPES.MATRIX]: QUESTION_LABELS.MATRIX,
  [QUESTION_TYPES.RANKING]: QUESTION_LABELS.RANKING,
  [QUESTION_TYPES.NPS]: QUESTION_LABELS.NPS
};

export const GROUPED_QUESTION_TYPES = {
  [QUESTION_LABELS.AGREE_DISAGREE]: [QUESTION_TYPES.ASKYOURTEAM_SCALE, QUESTION_TYPES.ZERO_TO_SIX],
  [QUESTION_LABELS.ZERO_TO_TEN]: [QUESTION_TYPES.ZERO_TO_TEN],
  [QUESTION_LABELS.ZERO_TO_THREE]: [QUESTION_TYPES.ZERO_TO_THREE],
  [QUESTION_LABELS.ZERO_TO_FIVE]: [QUESTION_TYPES.ZERO_TO_FIVE],
  [QUESTION_LABELS.YES_NO]: [QUESTION_TYPES.YES_NO],
  [QUESTION_LABELS.MULTIPLE_CHOICE]: [QUESTION_TYPES.MULTIPLE_CHOICE, QUESTION_TYPES.CATEGORISED],
  [QUESTION_LABELS.FREE_TEXT]: [QUESTION_TYPES.FREE_TEXT],
  [QUESTION_LABELS.MATRIX]: [QUESTION_TYPES.MATRIX],
  [QUESTION_LABELS.CATEGORISED]: [QUESTION_TYPES.CATEGORISED],
  [QUESTION_LABELS.RANKING]: [QUESTION_TYPES.RANKING],
};

export const QUESTION_SCALE_TYPE = {
  CONTINUOUS: 'continuous',
  DISCRETE: 'discrete',
};

export const LIKERT_TYPES = {
  SIMPLE: 'simple',
  SMILES: 'smiles'
};

export const QUESTION_TYPE_SUPPORTS_IMAGE = [
  QUESTION_TYPES.ASKYOURTEAM_SCALE,
  QUESTION_TYPES.ZERO_TO_SIX,
  QUESTION_TYPES.ZERO_TO_TEN,
  QUESTION_TYPES.YES_NO,
  QUESTION_TYPES.FREE_TEXT,
  QUESTION_TYPES.MULTIPLE_CHOICE
];

export const PRODUCTS = {
  BENCHMARKING: 'Benchmarking',
  ADVANCED_BENCHMARKING: 'Advanced benchmarking',
  SUPPORT_HOURS: 'Support Hours',
  PARTICIPANT_COUNT: 'Participant Count',
};

const GENERATE_COLOUR_SCALE = props => {
  const {
    type,
    ...options
  } = props;
  let scaleFunc = (() => {
    switch (type) {
      case 'threshold': return scaleThreshold;
      case 'ordinal': return scaleOrdinal;
      case 'linear': return scaleLinear;
      default: return scaleLinear;
    }
  })();
  return scaleFunc(options);
};

export const CHANGE_CHART_THEME = newTheme => {
  const {
    colours: {
      red: RED,
      green: GREEN,
      blue: BLUE,
      yellow: YELLOW,
      purple: PURPLE,
      orange: ORANGE
    } = {},
    scale_0_to_100,
    scale_heatmap,
    scale_heatmap_yes,
    scale_heatmap_no
  } = newTheme || {};

  SYSTEM_COLOR.CHART_THEME = {
    ...SYSTEM_COLOR.CHART_THEME,
    ...RED && ({ RED }),
    ...GREEN && ({ GREEN }),
    ...BLUE && ({ BLUE }),
    ...YELLOW && ({ YELLOW }),
    ...PURPLE && ({ PURPLE }),
    ...ORANGE && ({ ORANGE }),
    ...scale_0_to_100 && ({
      COLOUR_SCALE_100: GENERATE_COLOUR_SCALE(scale_0_to_100),
      COLOUR_SCALE_10: GENERATE_COLOUR_SCALE({
        ...scale_0_to_100,
        domain: scale_0_to_100.domain.map(x => x / 10)
      })
    }),
    ...scale_heatmap && ({
      COLOUR_SCALE_HEATMAP: GENERATE_COLOUR_SCALE(scale_heatmap),
      COLOUR_SCALE_HEATMAP_0_10: GENERATE_COLOUR_SCALE({
        ...scale_heatmap,
        domain: scale_heatmap.domain.map(x => x / 10)
      })
    }),
    ...scale_heatmap_yes && ({ COLOUR_SCALE_HEATMAP_YES: GENERATE_COLOUR_SCALE(scale_heatmap_yes) }),
    ...scale_heatmap_no && ({ COLOUR_SCALE_HEATMAP_NO: GENERATE_COLOUR_SCALE(scale_heatmap_no) })
  };
};

CHANGE_CHART_THEME({
  colours: {
    red: SYSTEM_COLOR.GREY.DARK,
    green: SYSTEM_COLOR.GREY.DARK,
    blue: SYSTEM_COLOR.GREY.DARK,
    yellow: SYSTEM_COLOR.GREY.DARK,
    purple: SYSTEM_COLOR.GREY.DARK,
    orange: SYSTEM_COLOR.GREY.DARK
  },
  scale_0_to_100: {
    type: 'linear',
    range: [SYSTEM_COLOR.GREY.DARK, SYSTEM_COLOR.GREY.DARK],
    domain: [0, 100]
  },
  scale_heatmap: {
    type: 'linear',
    range: [SYSTEM_COLOR.GREY.DARK, SYSTEM_COLOR.GREY.LIGHT_X, SYSTEM_COLOR.GREY.DARK],
    domain: [0, 50, 100]
  },
  scale_heatmap_yes: {
    type: 'linear',
    range: [SYSTEM_COLOR.GREY.LIGHT_X, SYSTEM_COLOR.GREY.DARK],
    domain: [0, 100]
  },
  scale_heatmap_no: {
    type: 'linear',
    range: [SYSTEM_COLOR.GREY.LIGHT_X, SYSTEM_COLOR.GREY.DARK],
    domain: [0, 100]
  }
});

export const FEEDBACK_SERVICE_BACKGROUND_COLOUR = '#ffffff';

export const SURVEY_FLOW_VALUES = {
  INFORMATION: 'survey_info',
  SETTINGS: 'survey_settings',
  QUESTIONS: 'survey_questions',
  DEMOGRAPHICS: 'survey_categories',
  PARTICIPANTS: 'survey_participants',
  COMMUNICATIONS: 'survey_introduction',
  OVERVIEW: 'survey_overview',
  COMPLETED: 'completed'
};

export const SURVEY_STATE_VALUES = {
  DRAFT: 'draft',
  PENDING: 'pending',
  ACTIVE: 'active',
  CLOSED: 'closed'
};

export const SURVEY_STATUSES = [
  {
    value: SURVEY_STATE_VALUES.DRAFT,
    label: 'Draft'
  },
  {
    value: SURVEY_STATE_VALUES.PENDING,
    label: 'Pending'
  },
  {
    value: SURVEY_STATE_VALUES.ACTIVE,
    label: 'Active'
  },
  {
    value: SURVEY_STATE_VALUES.CLOSED,
    label: 'Closed'
  }
];

export const SURVEY_TYPE = {
  STANDARD: 'standard',
  OFFBOARDING: 'offboarding',
  ONBOARDING: 'onboarding',
  LEADERSHIP: 'leadership',
  ONGOING: 'ongoing',
  DEMOGRAPHIC_REVIEW: 'demographic_review',
};

export const INSIGHTS_COMPATIBLE_SURVEY_TYPES = [
  SURVEY_TYPE.STANDARD,
  SURVEY_TYPE.OFFBOARDING,
  SURVEY_TYPE.ONBOARDING,
  SURVEY_TYPE.ONGOING
];

export const NOTIFICATION_TYPE = {
  COMPLETION: 'completion',
  REMINDER: 'reminder',
  INVITATION: 'invitation',
  EXTENSION: 'extension'
};

export const DATE_NUMBERS = {
  SECOND: 1000,
  MINUTE: 1000 * 60,
  HOUR: 1000 * 60 * 60,
  DAY: 1000 * 60 * 60 * 24
};

export const SURVEY_TAKING_STATUS = {
  COMPLETED: 'Completed',
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress',
  UNSUBSCRIBED: 'Unsubscribed'
};

export const SURVEY_TAKING_STATUS_SENTENCE = {
  COMPLETED: 'Completed',
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  UNSUBSCRIBED: 'Unsubscribed'
};

export const INVITATION_STATUS = {
  NOT_APPLICABLE: 'Not applicable',
  BOUNCE: 'Bounce',
  SENT: 'Sent',
  PENDING: 'Pending',
  DROPPED: 'Dropped',
  DELIVERED: 'Delivered',
  DEFERRED: 'Deferred'
};

export const PARTICIPATION_STATUS_COLORS = {
  COMPLETED: SYSTEM_COLOR.CHART.GREEN,
  NOT_STARTED: SYSTEM_COLOR.CHART.RED,
  IN_PROGRESS: SYSTEM_COLOR.CHART.YELLOW,
  INVITED: SYSTEM_COLOR.GREY.DARK
};

export const TIME_ZONES_OPTIONS = [{ label: '(GMT-12:00) Etc/GMT+12', value: 'Etc/GMT+12' }, { label: '(GMT-11:00) Pacific/Midway', value: 'Pacific/Midway' }, { label: '(GMT-11:00) Pacific/Pago Pago', value: 'Pacific/Pago_Pago' }, { label: '(GMT-10:00) Pacific/Honolulu', value: 'Pacific/Honolulu' }, { label: '(GMT-09:00) America/Juneau', value: 'America/Juneau' }, { label: '(GMT-08:00) America/Los Angeles', value: 'America/Los_Angeles' }, { label: '(GMT-08:00) America/Tijuana', value: 'America/Tijuana' }, { label: '(GMT-07:00) America/Denver', value: 'America/Denver' }, { label: '(GMT-07:00) America/Phoenix', value: 'America/Phoenix' }, { label: '(GMT-07:00) America/Chihuahua', value: 'America/Chihuahua' }, { label: '(GMT-07:00) America/Mazatlan', value: 'America/Mazatlan' }, { label: '(GMT-06:00) America/Chicago', value: 'America/Chicago' }, { label: '(GMT-06:00) America/Regina', value: 'America/Regina' }, { label: '(GMT-06:00) America/Mexico City', value: 'America/Mexico_City' }, { label: '(GMT-06:00) America/Monterrey', value: 'America/Monterrey' }, { label: '(GMT-06:00) America/Guatemala', value: 'America/Guatemala' }, { label: '(GMT-05:00) America/New York', value: 'America/New_York' }, { label: '(GMT-05:00) America/Indiana/Indianapolis', value: 'America/Indiana/Indianapolis' }, { label: '(GMT-05:00) America/Bogota', value: 'America/Bogota' }, { label: '(GMT-05:00) America/Lima', value: 'America/Lima' }, { label: '(GMT-04:00) America/Halifax', value: 'America/Halifax' }, { label: '(GMT-04:00) America/Caracas', value: 'America/Caracas' }, { label: '(GMT-04:00) America/La Paz', value: 'America/La_Paz' }, { label: '(GMT-04:00) America/Santiago', value: 'America/Santiago' }, { label: '(GMT-03:30) America/St Johns', value: 'America/St_Johns' }, { label: '(GMT-03:00) America/Sao Paulo', value: 'America/Sao_Paulo' }, { label: '(GMT-03:00) America/Argentina/Buenos Aires', value: 'America/Argentina/Buenos_Aires' }, { label: '(GMT-03:00) America/Montevideo', value: 'America/Montevideo' }, { label: '(GMT-04:00) America/Guyana', value: 'America/Guyana' }, { label: '(GMT-04:00) America/Puerto Rico', value: 'America/Puerto_Rico' }, { label: '(GMT-03:00) America/Godthab', value: 'America/Godthab' }, { label: '(GMT-02:00) Atlantic/South Georgia', value: 'Atlantic/South_Georgia' }, { label: '(GMT-01:00) Atlantic/Azores', value: 'Atlantic/Azores' }, { label: '(GMT-01:00) Atlantic/Cape Verde', value: 'Atlantic/Cape_Verde' }, { label: '(GMT+00:00) Europe/Dublin', value: 'Europe/Dublin' }, { label: '(GMT+00:00) Europe/London', value: 'Europe/London' }, { label: '(GMT+00:00) Europe/Lisbon', value: 'Europe/Lisbon' }, { label: '(GMT+00:00) Africa/Casablanca', value: 'Africa/Casablanca' }, { label: '(GMT+00:00) Africa/Monrovia', value: 'Africa/Monrovia' }, { label: '(GMT+00:00) Etc/UTC', value: 'Etc/UTC' }, { label: '(GMT+01:00) Europe/Belgrade', value: 'Europe/Belgrade' }, { label: '(GMT+01:00) Europe/Bratislava', value: 'Europe/Bratislava' }, { label: '(GMT+01:00) Europe/Budapest', value: 'Europe/Budapest' }, { label: '(GMT+01:00) Europe/Ljubljana', value: 'Europe/Ljubljana' }, { label: '(GMT+01:00) Europe/Prague', value: 'Europe/Prague' }, { label: '(GMT+01:00) Europe/Sarajevo', value: 'Europe/Sarajevo' }, { label: '(GMT+01:00) Europe/Skopje', value: 'Europe/Skopje' }, { label: '(GMT+01:00) Europe/Warsaw', value: 'Europe/Warsaw' }, { label: '(GMT+01:00) Europe/Zagreb', value: 'Europe/Zagreb' }, { label: '(GMT+01:00) Europe/Brussels', value: 'Europe/Brussels' }, { label: '(GMT+01:00) Europe/Copenhagen', value: 'Europe/Copenhagen' }, { label: '(GMT+01:00) Europe/Madrid', value: 'Europe/Madrid' }, { label: '(GMT+01:00) Europe/Paris', value: 'Europe/Paris' }, { label: '(GMT+01:00) Europe/Amsterdam', value: 'Europe/Amsterdam' }, { label: '(GMT+01:00) Europe/Berlin', value: 'Europe/Berlin' }, { label: '(GMT+01:00) Europe/Zurich', value: 'Europe/Zurich' }, { label: '(GMT+01:00) Europe/Rome', value: 'Europe/Rome' }, { label: '(GMT+01:00) Europe/Stockholm', value: 'Europe/Stockholm' }, { label: '(GMT+01:00) Europe/Vienna', value: 'Europe/Vienna' }, { label: '(GMT+01:00) Africa/Algiers', value: 'Africa/Algiers' }, { label: '(GMT+02:00) Europe/Bucharest', value: 'Europe/Bucharest' }, { label: '(GMT+02:00) Africa/Cairo', value: 'Africa/Cairo' }, { label: '(GMT+02:00) Europe/Helsinki', value: 'Europe/Helsinki' }, { label: '(GMT+02:00) Europe/Kiev', value: 'Europe/Kiev' }, { label: '(GMT+02:00) Europe/Riga', value: 'Europe/Riga' }, { label: '(GMT+02:00) Europe/Sofia', value: 'Europe/Sofia' }, { label: '(GMT+02:00) Europe/Tallinn', value: 'Europe/Tallinn' }, { label: '(GMT+02:00) Europe/Vilnius', value: 'Europe/Vilnius' }, { label: '(GMT+02:00) Europe/Athens', value: 'Europe/Athens' }, { label: '(GMT+03:00) Europe/Istanbul', value: 'Europe/Istanbul' }, { label: '(GMT+03:00) Europe/Minsk', value: 'Europe/Minsk' }, { label: '(GMT+02:00) Asia/Jerusalem', value: 'Asia/Jerusalem' }, { label: '(GMT+02:00) Africa/Harare', value: 'Africa/Harare' }, { label: '(GMT+02:00) Africa/Johannesburg', value: 'Africa/Johannesburg' }, { label: '(GMT+02:00) Europe/Kaliningrad', value: 'Europe/Kaliningrad' }, { label: '(GMT+03:00) Europe/Moscow', value: 'Europe/Moscow' }, { label: '(GMT+04:00) Europe/Volgograd', value: 'Europe/Volgograd' }, { label: '(GMT+04:00) Europe/Samara', value: 'Europe/Samara' }, { label: '(GMT+03:00) Asia/Kuwait', value: 'Asia/Kuwait' }, { label: '(GMT+03:00) Asia/Riyadh', value: 'Asia/Riyadh' }, { label: '(GMT+03:00) Africa/Nairobi', value: 'Africa/Nairobi' }, { label: '(GMT+03:00) Asia/Baghdad', value: 'Asia/Baghdad' }, { label: '(GMT+03:30) Asia/Tehran', value: 'Asia/Tehran' }, { label: '(GMT+04:00) Asia/Muscat', value: 'Asia/Muscat' }, { label: '(GMT+04:00) Asia/Baku', value: 'Asia/Baku' }, { label: '(GMT+04:00) Asia/Tbilisi', value: 'Asia/Tbilisi' }, { label: '(GMT+04:00) Asia/Yerevan', value: 'Asia/Yerevan' }, { label: '(GMT+04:30) Asia/Kabul', value: 'Asia/Kabul' }, { label: '(GMT+05:00) Asia/Yekaterinburg', value: 'Asia/Yekaterinburg' }, { label: '(GMT+05:00) Asia/Karachi', value: 'Asia/Karachi' }, { label: '(GMT+05:00) Asia/Tashkent', value: 'Asia/Tashkent' }, { label: '(GMT+05:30) Asia/Kolkata', value: 'Asia/Kolkata' }, { label: '(GMT+05:45) Asia/Kathmandu', value: 'Asia/Kathmandu' }, { label: '(GMT+06:00) Asia/Dhaka', value: 'Asia/Dhaka' }, { label: '(GMT+05:30) Asia/Colombo', value: 'Asia/Colombo' }, { label: '(GMT+06:00) Asia/Almaty', value: 'Asia/Almaty' }, { label: '(GMT+07:00) Asia/Novosibirsk', value: 'Asia/Novosibirsk' }, { label: '(GMT+06:30) Asia/Rangoon', value: 'Asia/Rangoon' }, { label: '(GMT+07:00) Asia/Bangkok', value: 'Asia/Bangkok' }, { label: '(GMT+07:00) Asia/Jakarta', value: 'Asia/Jakarta' }, { label: '(GMT+07:00) Asia/Krasnoyarsk', value: 'Asia/Krasnoyarsk' }, { label: '(GMT+08:00) Asia/Shanghai', value: 'Asia/Shanghai' }, { label: '(GMT+08:00) Asia/Chongqing', value: 'Asia/Chongqing' }, { label: '(GMT+08:00) Asia/Hong Kong', value: 'Asia/Hong_Kong' }, { label: '(GMT+06:00) Asia/Urumqi', value: 'Asia/Urumqi' }, { label: '(GMT+08:00) Asia/Kuala Lumpur', value: 'Asia/Kuala_Lumpur' }, { label: '(GMT+08:00) Asia/Singapore', value: 'Asia/Singapore' }, { label: '(GMT+08:00) Asia/Taipei', value: 'Asia/Taipei' }, { label: '(GMT+08:00) Australia/Perth', value: 'Australia/Perth' }, { label: '(GMT+08:00) Asia/Irkutsk', value: 'Asia/Irkutsk' }, { label: '(GMT+08:00) Asia/Ulaanbaatar', value: 'Asia/Ulaanbaatar' }, { label: '(GMT+09:00) Asia/Seoul', value: 'Asia/Seoul' }, { label: '(GMT+09:00) Asia/Tokyo', value: 'Asia/Tokyo' }, { label: '(GMT+09:00) Asia/Yakutsk', value: 'Asia/Yakutsk' }, { label: '(GMT+09:30) Australia/Darwin', value: 'Australia/Darwin' }, { label: '(GMT+09:30) Australia/Adelaide', value: 'Australia/Adelaide' }, { label: '(GMT+10:00) Australia/Melbourne', value: 'Australia/Melbourne' }, { label: '(GMT+10:00) Australia/Sydney', value: 'Australia/Sydney' }, { label: '(GMT+10:00) Australia/Brisbane', value: 'Australia/Brisbane' }, { label: '(GMT+10:00) Australia/Hobart', value: 'Australia/Hobart' }, { label: '(GMT+10:00) Asia/Vladivostok', value: 'Asia/Vladivostok' }, { label: '(GMT+10:00) Pacific/Guam', value: 'Pacific/Guam' }, { label: '(GMT+10:00) Pacific/Port Moresby', value: 'Pacific/Port_Moresby' }, { label: '(GMT+11:00) Asia/Magadan', value: 'Asia/Magadan' }, { label: '(GMT+11:00) Asia/Srednekolymsk', value: 'Asia/Srednekolymsk' }, { label: '(GMT+11:00) Pacific/Guadalcanal', value: 'Pacific/Guadalcanal' }, { label: '(GMT+11:00) Pacific/Noumea', value: 'Pacific/Noumea' }, { label: '(GMT+12:00) Pacific/Fiji', value: 'Pacific/Fiji' }, { label: '(GMT+12:00) Asia/Kamchatka', value: 'Asia/Kamchatka' }, { label: '(GMT+12:00) Pacific/Majuro', value: 'Pacific/Majuro' }, { label: '(GMT+12:00) Pacific/Auckland', value: 'Pacific/Auckland' }, { label: '(GMT+13:00) Pacific/Tongatapu', value: 'Pacific/Tongatapu' }, { label: '(GMT+13:00) Pacific/Fakaofo', value: 'Pacific/Fakaofo' }, { label: '(GMT+12:45) Pacific/Chatham', value: 'Pacific/Chatham' }, { label: '(GMT+13:00) Pacific/Apia', value: 'Pacific/Apia' }];

export const LOCALE_CODES = {
  'ar': 'Arabic',
  'bi': 'Bislama',
  'bn': 'Bengali',
  'en': 'English',
  'es': 'Spanish',
  'es-CL': 'Spanish (Chile)',
  'es-MX': 'Spanish (Mexico)',
  'fil': 'Filipino',
  'fr': 'French',
  'hi': 'Hindi',
  'id': 'Indonesian',
  'mi-NZ': 'Te Reo',
  'pa': 'Punjabi',
  'pis': 'SI Pijin',
  'pt-BR': 'Português (Brasil)',
  'ro': 'Romanian',
  'sm': 'Samoan',
  'th': 'Thai',
  'to': 'Tongan',
  'vi-VN': 'Vietnamese',
  'zh-CN': 'Simplified Chinese'
};

export const REPORT_TYPES = {
  INSIGHTS: 'insights',
  TEXT_ANALYSIS: 'text_analysis',
  BENCHMARKING: 'benchmarking',
  BENCHMARKS_PREVIEW: 'benchmarks_preview',
  ADVANCED_BENCHMARKING: 'advanced_benchmarking',
  CONSULTANCY: 'consultancy',
  CROSS_TAB: 'cross_tabular',
  PARTICIPATION: 'participation',
  QUESTIONS: 'questions',
  AGREE_DISAGREE: 'zero_to_six',
  ZERO_TO_THREE: 'zero_to_three',
  ZERO_TO_FIVE: 'zero_to_five',
  YES_NO: 'yes_no',
  ZERO_TO_TEN: 'zero_to_ten',
  NPS: 'nps',
  MULTIPLE_CHOICE: 'multiple_choice',
  MATRIX: 'matrix',
  RANKING: 'ranking',
  HEATMAP: 'heatmap',
  ALL_LEADERS: 'all_leaders',
  ALL_SERVICES: 'all_services',
  DEMOGRAPHIC_AVERAGES: 'demographic_averages',
  ALIGNMENT: 'alignment',
  LEADER_ALIGNMENT: 'leader_alignment',
  SERVICE_ALIGNMENT: 'service_alignment',
  SURVEY_ACTIONS: 'survey_actions',
  OVER_TIME: 'over_time',
  QUICKSIGHT: 'quicksight_report',
  STARRED_COMMENTS: 'starred_comments'
};

export const REPORT_STATUS = {
  AVAILABLE: 'available',
  BETA: 'beta',
  NEW: 'new',
};

export const BRANCHABLE_TYPE = {
  QUESTION: 'Question',
  GROUP: 'QuestionGroup'
};

export const SERVER_TASK_STATUS = {
  NOT_STARTED: 'not started',
  RUNNING: 'RUNNING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED'
};

export const CUSTOM_STATUS_CODES = {
  DECISION_REQUIRED: 432
};

// NOTE: there was a mention in meeting about 'Self' being the top option, and also a potential preferred order of the breakdown to appear.
// need to figure this out, note it down, and make the required changes based on Product decision.
export const SPECIAL_DEMOGRAPHICS = {
  LEVEL: {
    ID: 'Level',
    EXECUTIVE: 'Executive',
    NON_EXECUTIVE: 'Non-executive'
  },
  LEADERSHIP_PEOPLE_LEADER: {
    ID: 'Leader'
  },
  LEADERSHIP_RELATION: {
    ID: 'Relation',
    FEEDBACK_GROUP: 'Feedback group',
    SELF: 'Self',
    DIRECT_REPORT: 'Direct report',
    MANAGER: 'Manager',
    PEER: 'Peer',
    OTHER: 'Other'
  },
  RESPONDENT_OPTION_GROUP: {
    ID: 'Participating Service'
  },
  RESPONDENT_OPTION_SUBGROUP: {
    ID: 'Relation',
    SERVICE_STAFF: 'Service staff',
    SELF: 'Self',
    SUPPORT_STAFF: 'Support staff',
    SERVICE_LEADER: 'Service leader',
    EXECUTIVE: 'Executive',
    OTHER: 'Other'
  }
};

export const RESPONDENT_GROUP_STATUS = {
  RESPONDENT_GROUP_STATUS_DRAFT: 'draft',
  RESPONDENT_GROUP_STATUS_PENDING: 'pending',
  RESPONDENT_GROUP_STATUS_ACTIVE: 'active',
  RESPONDENT_GROUP_STATUS_CLOSED: 'closed'
};

export const LEADERSHIP_BREAKDOWN_TYPES = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
};

export const SURVEY_LIST_TYPES = {
  PARENT: 'parent',
  NESTED: 'nested'
};

export const TEXT_TEMPLATE_TYPES = {
  DEFAULT: 'default',
  SELF_ASSESSMENT: 'self-assessment',
  STAFF_LEADERS: 'staff-and-leader',
};

// System-defined demographics created during account setup
// These match the demographics created in Account::AccountCallbacks#create_system_defined_demographics
export const SYSTEM_DEMOGRAPHICS = ['gender', 'age', 'length_of_service', 'level'];

// Demographics that should show as "Recommended" in the UI
export const RECOMMENDED_DEMOGRAPHICS = ['age', 'length_of_service', 'level'];

export const MULTI_SELECT_DEMOGRAPHIC_DELIMITER = ';';

export const SES_BOUNCE_TYPES = {
  INVALID_EMAIL: 'smtp; 550',
  SUPPRESSION_LIST: 'Amazon SES did not send the message to this address because it is on the suppression list for your account',
};

export const ERROR_LEVEL = {
  INFO: 'info',
  ERROR: 'error'
};

export const SURVEY_TEMPLATE_NAMES = {
  BUILD_YOUR_OWN: 'build_your_own'
};

export const SURVEY_TEMPLATE_DESCRIPTIONS = {
  TEMPLATES: 'AskYourTeam validated survey templates are based on leading international management and leadership frameworks that are proven drivers of organisational success. Our templates are co-designed with topic and sector experts and peer reviewed by the University of Waikato\'s New Zealand Institute for Business Research. We recommend using a survey template in it\'s entirety to ensure coverage of all key aspects of the survey topic.',
  QUESTIONS: 'Browse and select individual questions from the survey templates included in your subscription. Use the filters to narrow down by category or template.',
};
